import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { IconSpinner, Modal } from '../../../components';
import useBfcacheRestore from '../../../hooks/useBfcacheRestore';
import {
  PAYMENT_METHODS,
  PAYMENT_METHODS_PRICE_EUR,
  PAYMENT_METHODS_WEEKS,
} from '../../../constants/payment.constants';
import { USER_TYPE } from '../../../constants/user.constants';
import { DURATION_OPTIONS } from '../../CreateListingDurationPage/CreateListingDurationForm';
import subscriptionsApi from '../../../api/subscriptions';
import { fetchCurrentUser } from '../../../ducks/user.duck';
import {
  deriveSubscriptionDialog,
  SUB_ACTION,
  SUB_TONE,
} from '../ManageListingCard/listingCardSubscription';
import { hasBillingToStop } from '../ManageListingCard/listingCardState';
import { clearSubscriptionCancelError } from '../ManageListingsPage.duck';

import css from './SubscriptionDetailsModal.module.css';

// Map known BE error codes to localized messages. Falls back to the server's
// own message for other client (4xx) errors, then to a generic translated
// message for server (5xx) errors so internals aren't shown to users.
const ERROR_CODE_TRANSLATIONS = {
  FLOATING_SUBSCRIPTION_EXISTS: 'SubscriptionDetailsModal.floatingSubscriptionError',
  SUBSCRIPTION_PROCESSING: 'SubscriptionDetailsModal.subscriptionProcessingError',
  // Ended for good. Stripe cannot revive a cancelled subscription, so this points at
  // starting a new one rather than at a retry that can never succeed.
  SUBSCRIPTION_NOT_RENEWABLE: 'SubscriptionDetailsModal.notRenewableError',
};

const getActionErrorText = (error, intl) => {
  const data = error?.response?.data;
  const translationId = data?.code && ERROR_CODE_TRANSLATIONS[data.code];
  if (translationId) {
    return intl.formatMessage({ id: translationId });
  }

  const status = error?.response?.status;
  if (status && status < 500) {
    const message = data?.message;
    const text = Array.isArray(message) ? message.join(', ') : message;
    if (text) return text;
  }

  return intl.formatMessage({ id: 'SubscriptionDetailsModal.actionError' });
};

const TONE_CLASS = {
  [SUB_TONE.POSITIVE]: css.tonePositive,
  [SUB_TONE.WARNING]: css.toneWarning,
  [SUB_TONE.NEGATIVE]: css.toneNegative,
  [SUB_TONE.NEUTRAL]: css.toneNeutral,
};

const BANNER_CLASS = {
  [SUB_TONE.WARNING]: css.bannerWarning,
  [SUB_TONE.NEGATIVE]: css.bannerNegative,
};

/**
 * "Abonnement beheren" — what the subscription is, and what can be done to it.
 *
 * The contents come from the same CARD_STATE the card row used, so the dialog can
 * never contradict the line the seller just read to get here.
 *
 * Facts before actions on purpose: a seller opening this is usually answering "am I
 * still being charged, and when", not looking for a button.
 */
const SubscriptionDetailsModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    listingId,
    subscription,
    cardState,
    hasCancelFailed = false,
    hasCancelScheduled = false,
  } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const { currentUser } = useSelector(state => state.user);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isBusinessSeller = userType === USER_TYPE.BUSINESS_SELLER;

  const [selectedPlan, setSelectedPlan] = useState(PAYMENT_METHODS.PRIVATE.QUARTERLY);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Read from the subscription, not from the fact that the boat is archived: a
  // cancellation that failed leaves an archived advertisement still being charged for,
  // and that is exactly when this dialog has to offer the retry.
  const dialog = deriveSubscriptionDialog({
    state: cardState,
    hasCancelFailed,
    hasCancelScheduled,
    hasLiveBilling: hasBillingToStop(subscription),
  });

  // Coming back from Stripe with the browser's back button restores this dialog exactly
  // as it was — including the spinner and the disabled rows we set on the way out.
  // Clear them, or the seller is left staring at a row that never resolves.
  useBfcacheRestore(useCallback(() => setPendingAction(null), []));

  const handleClose = () => {
    setActionError(null);
    setPendingAction(null);
    onClose();
  };

  /**
   * Portal actions navigate away; the in-app ones resolve here. Either way the button
   * stays busy until something happens, so a slow response cannot be double-clicked
   * into two requests.
   */
  const run = async (key, call, { navigates = false } = {}) => {
    setPendingAction(key);
    setActionError(null);
    try {
      const response = await call();
      const url = response?.data?.url;
      if (navigates && url) {
        window.location.href = url;
        return;
      }

      // An in-app change has landed, so refresh before closing. The card reads the
      // subscription off `currentUser` metadata, and closing a dialog refetches
      // nothing on its own — without this the seller cancels and the card behind the
      // dialog still promises the next charge until they reload the page.
      //
      // `enforce` matters: fetchCurrentUser throttles repeat calls and would otherwise
      // skip this one entirely.
      await dispatch(fetchCurrentUser({ enforce: true }));

      // A cancellation that has now gone through must take the card's red warning strip
      // with it. This is the path the strip's own "Probeer opnieuw" leads to, and the
      // retry used to succeed while the strip stayed put — the seller had no way to make
      // it go away short of reloading, and every reason to believe they were still being
      // billed. Nothing else in the app clears this error.
      if (key === SUB_ACTION.CANCEL) {
        dispatch(clearSubscriptionCancelError());
      }

      handleClose();
    } catch (e) {
      setActionError(e);
      setPendingAction(null);
    }
  };

  const handleAction = key => {
    switch (key) {
      case SUB_ACTION.PLAN:
        return run(key, () => subscriptionsApi.change(listingId), { navigates: true });
      case SUB_ACTION.PAYMENT_METHOD:
      case SUB_ACTION.INVOICES:
        return run(key, () => subscriptionsApi.portal(), { navigates: true });
      // Lifts a *pending* cancellation, so it only appears while the subscription is
      // still alive. An expired one cannot be resumed — that path shows the plan
      // selector and buys a new subscription instead.
      case SUB_ACTION.RESUME:
        return run(key, () => subscriptionsApi.renew(listingId), { navigates: true });
      // In-app, because the seller has read the consequence on the row itself and
      // cancelling at period end is reversible via "Opzegging ongedaan maken".
      case SUB_ACTION.CANCEL:
        return run(key, () => subscriptionsApi.cancel(listingId));
      default:
        return undefined;
    }
  };

  const planLabel = subscription?.plan ? (
    <FormattedMessage id={`General.paymentMethods.${subscription.plan}`} />
  ) : (
    '–'
  );

  const priceLabel = (() => {
    const amount = PAYMENT_METHODS_PRICE_EUR[subscription?.plan];
    const weeks = PAYMENT_METHODS_WEEKS[subscription?.plan];
    if (amount == null || weeks == null) {
      return '–';
    }
    return (
      <FormattedMessage
        id="SubscriptionDetailsModal.pricePerWeeks"
        values={{
          price: intl.formatNumber(amount, { style: 'currency', currency: 'EUR' }),
          weeks,
        }}
      />
    );
  })();

  // `expiresAt` is the end of the paid period; what that *means* is carried by the
  // fact's label, which changes per state.
  const dateLabel = subscription?.expiresAt
    ? intl.formatDate(new Date(subscription.expiresAt), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '–';

  const factValue = fact => {
    switch (fact.kind) {
      case 'plan':
        return planLabel;
      case 'price':
        return priceLabel;
      case 'date':
        return dateLabel;
      default:
        return <FormattedMessage id={fact.valueId} />;
    }
  };

  /** No subscription at all: offer to buy one rather than showing empty facts. */
  const renderPlanSelector = () => (
    <div className={css.expiredSection}>
      <p className={css.expiredMessage}>
        <FormattedMessage id="SubscriptionDetailsModal.expiredMessage" />
      </p>
      <div className={css.plansList}>
        {DURATION_OPTIONS.map(({ value, titleKey, taglineKey, priceTextKey, isMostChosen }) => {
          const isSelected = selectedPlan === value;
          return (
            <label
              key={value}
              className={`${css.planCard} ${isSelected ? css.planCardSelected : ''}`}
            >
              <input
                type="radio"
                name="subscriptionPlan"
                value={value}
                checked={isSelected}
                onChange={() => setSelectedPlan(value)}
                className={css.planRadioInput}
              />
              <div className={css.planHeader}>
                <div className={css.planLeft}>
                  <span
                    className={`${css.radioCircle} ${isSelected ? css.radioCircleChecked : ''}`}
                  />
                  <span className={css.planTitle}>
                    <FormattedMessage id={titleKey} />
                  </span>
                </div>
                {isMostChosen && (
                  <span className={css.mostChosen}>
                    <FormattedMessage id="CreateListingDurationPage.mostChosen" />
                  </span>
                )}
              </div>
              <p className={css.planTagline}>
                <FormattedMessage id={taglineKey} />
              </p>
              <p className={css.planPriceText}>
                <FormattedMessage id={priceTextKey} />
              </p>
            </label>
          );
        })}
      </div>
      {actionError && <p className={css.errorMessage}>{getActionErrorText(actionError, intl)}</p>}
      <button
        className={css.subscribeButton}
        onClick={() =>
          run('subscribe', () => subscriptionsApi.subscribe({ listingId, plan: selectedPlan }), {
            navigates: true,
          })
        }
        disabled={!!pendingAction || !selectedPlan}
      >
        {pendingAction ? (
          <IconSpinner />
        ) : (
          <FormattedMessage id="SubscriptionDetailsModal.subscribe" />
        )}
      </button>
    </div>
  );

  const renderContent = () => {
    // No subscription, nothing manageable, or one that has run out — all three mean the
    // only thing worth showing is what a new subscription costs.
    if (!subscription || !dialog.isManageable || dialog.needsNewSubscription) {
      return renderPlanSelector();
    }

    return (
      <>
        {/* Shown here as well as on the card, because sellers close dialogs fast and
            this one must not be missed: the advertisement is stopped, the money is not. */}
        {dialog.showFailure && (
          <div className={css.failureBlock}>
            <span className={css.failureTitle}>
              <FormattedMessage id="SubscriptionDetailsModal.cancelFailedTitle" />
            </span>
            <span className={css.failureBody}>
              <FormattedMessage
                id="SubscriptionDetailsModal.cancelFailedBody"
                values={{ date: dateLabel }}
              />
            </span>
            <button
              type="button"
              className={css.failureRetry}
              disabled={!!pendingAction}
              // Straight to the cancel action. This used to prefer an `onRetryCancel`
              // prop that no caller ever passed, so the fallback was the only live path
              // and the prop only made the retry look wired to something it wasn't.
              onClick={() => handleAction(SUB_ACTION.CANCEL)}
            >
              <FormattedMessage id="SubscriptionDetailsModal.retry" />
            </button>
          </div>
        )}

        {dialog.banner && (
          <div className={classNames(css.banner, BANNER_CLASS[dialog.banner.tone])}>
            <FormattedMessage id={dialog.banner.id} values={{ date: dateLabel }} />
          </div>
        )}

        <div className={css.factsGrid}>
          {dialog.facts.map(fact => (
            <div key={fact.key} className={css.fact}>
              <span className={css.factLabel}>
                <FormattedMessage id={fact.labelId} />
              </span>
              <span className={classNames(css.factValue, TONE_CLASS[fact.tone])}>
                {factValue(fact)}
              </span>
            </div>
          ))}
        </div>

        {actionError && <p className={css.errorMessage}>{getActionErrorText(actionError, intl)}</p>}

        <div className={css.actionList}>
          {dialog.actions.map(action => (
            <button
              key={action.key}
              type="button"
              className={classNames(css.actionRow, {
                [css.actionRowDestructive]: action.isDestructive,
              })}
              disabled={!!pendingAction}
              onClick={() => handleAction(action.key)}
            >
              <span className={css.actionText}>
                <span className={css.actionLabel}>
                  <FormattedMessage id={action.labelId} />
                </span>
                <span className={css.actionSubtitle}>
                  <FormattedMessage id={action.subtitleId} values={{ date: dateLabel }} />
                </span>
              </span>
              {pendingAction === action.key ? (
                <IconSpinner />
              ) : (
                <span className={css.actionChevron} aria-hidden="true">
                  ›
                </span>
              )}
            </button>
          ))}
        </div>

        {isBusinessSeller && (
          <p className={css.businessNote}>
            <FormattedMessage id="SubscriptionDetailsModal.businessNote" />
          </p>
        )}
      </>
    );
  };

  return (
    <Modal
      id={id}
      containerClassName={css.root}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'Modal.close' })}
    >
      <h2 className={css.title}>
        <FormattedMessage id="SubscriptionDetailsModal.title" />
      </h2>
      {renderContent()}
    </Modal>
  );
};

export default SubscriptionDetailsModal;
